import { Checkbox, FormControlLabel, Grid, InputAdornment, Link, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useInvestAssistantContext } from "../ContextType";
import { default as AccountCircle } from "@mui/icons-material/AccountCircle"
import { default as AlternateEmail } from "@mui/icons-material/AlternateEmail"
import { default as PhoneAndroid } from "@mui/icons-material/PhoneAndroid"
import { default as PlaceOutlined } from "@mui/icons-material/PlaceOutlined"
import { isNameValid, validateMail, validateMobile } from "../Validators";
import { Slider } from "../Slider";
import { FooterButtons } from "../FooterButtons";
import { styled } from "@mui/material/styles";
import { NewContact } from "../../../models/Contact";
import { ErlService } from "../../../service/ErlService";
import { ApiResponse } from "../../../models/ApiResponse";
import { fireEvent, trackLead } from "../../Tracking/FireEvent";
import { Label } from "../Label";

const BUNDESLAENDER = [
    'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen',
    'Hamburg', 'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen',
    'Nordrhein-Westfalen', 'Rheinland-Pfalz', 'Saarland', 'Sachsen',
    'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen',
];

const NewsletterConsentText = styled(Typography)(
    ({ theme }) => `
            font-size: 11px;
            line-height: 16px;
            color: #262527;
          `);

const NewsletterConsentLink = styled(Link)(
    ({ theme }) => `
            font-size: 11px;
            line-height: 16px;
            opacity: 0.6;
            color: #404040;
            text-decoration: underline;
            cursor: pointer;
          `);

const DataPrivacyNotice = styled(Typography)(
    ({ theme }) => `
        margin-bottom: 0;
        font-size: .8rem;
    `);

// Hint sitting between label and field, so every field block ends with the input
// and the vertical rhythm between the fields stays even.
const FieldHint = styled(Typography)(
    ({ theme }) => `
        margin: 0 0 8px !important;
        font-size: 12px;
        line-height: 16px;
        color: #6f7a81;
    `);

// Consent for forwarding out-of-Bavaria requests to IID (required by data protection).
const IidConsentBox = styled('div', {
    shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError: boolean }>(
    ({ hasError }) => `
        background-color: #ffffff;
        border: 1px solid ${hasError ? '#d32f2f' : '#c9cdd6'};
        border-radius: 8px;
        padding: 8px 12px;
    `);

const IidConsentText = styled(Typography)(
    ({ theme }) => `
        margin: 0 !important;
        font-size: 13px;
        line-height: 18px;
        color: #262527;
    `);

const IidConsentError = styled(Typography)(
    ({ theme }) => `
        margin: 6px 0 0 !important;
        font-size: 12px;
        line-height: 16px;
        color: #d32f2f;
    `);

// Boxed / outlined input that overrides the global underline-only MuiInputBase theme
const FieldInput = styled(TextField)(
    ({ theme }) => `
        & .MuiInputBase-root {
            background-color: #ffffff;
            border: 1px solid #c9cdd6 !important;
            border-radius: 8px !important;
            padding: 2px 12px;
            transition: border-color .15s ease, box-shadow .15s ease;
        }
        & .MuiInputBase-root:hover {
            border-color: #262527 !important;
        }
        & .MuiInputBase-root.Mui-focused {
            border-color: #262527 !important;
            box-shadow: 0 0 0 3px rgba(38, 37, 39, .10);
        }
        & .MuiInputBase-root.Mui-error {
            border-color: #d32f2f !important;
        }
        & .MuiInputBase-root:before,
        & .MuiInputBase-root:after {
            display: none !important;
        }
        & .MuiInputBase-input {
            padding: 11px 4px;
            color: #000000;
        }
    `);

export function Stage2() {

    const {
        yourName, setYourName, yourMail, setYourMail, yourMobile, setYourMobile,
        setNewsletterConsent, newsletterConsent,
        source, selectedPath, bundesland, setBundesland,
        iidConsent, setIidConsent
    } = useInvestAssistantContext();

    const [formSubmitted, setFormSubmitted] = useState(false);

    // Focus the first field once the slide-in transition has finished.
    const nameInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const t = setTimeout(() => nameInputRef.current?.focus(), 450);
        return () => clearTimeout(t);
    }, []);

    const isMailEmpty = !yourMail || yourMail.trim() === '';
    const isMobileEmpty = !yourMobile || yourMobile.trim() === '';

    const isMailValid = validateMail(yourMail);
    const isMobileValid = validateMobile(yourMobile);

    // Bundesland is only asked (and required) on the advisory paths, not for the newsletter.
    const isBundeslandRequired = selectedPath !== 'newsletter';
    const isBundeslandEmpty = !bundesland || bundesland.trim() === '';

    // Requests from outside Bavaria are handed over to IID for further processing.
    // The user has to acknowledge that before we may submit the data.
    const isIidConsentRequired = isBundeslandRequired && !isBundeslandEmpty && bundesland !== 'Bayern';
    const isIidConsentMissing = isIidConsentRequired && !iidConsent;

    const isValid = () => {
        if (!isNameValid(yourName)) return false;

        // If mail is provided, it must be valid
        if (!isMailEmpty && !isMailValid) return false;

        // If mobile is provided, it must be valid
        if (!isMobileEmpty && !isMobileValid) return false;

        // Bundesland is required on advisory paths
        if (isBundeslandRequired && isBundeslandEmpty) return false;

        // Outside Bavaria the hand-over to IID has to be acknowledged
        if (isIidConsentMissing) return false;

        // At least one must be provided and valid
        if ((!isMailEmpty && isMailValid) || (!isMobileEmpty && isMobileValid)) {
            return true;
        }

        return false;
    }

    const saveInquiry = (resolve: any) => {

        const newContact = new NewContact(
            undefined,
            undefined,
            undefined,
            undefined,
            yourName,
            yourMail,
            yourMobile,
            newsletterConsent,
            source,
            selectedPath,
            bundesland,
            isIidConsentRequired ? iidConsent : false
        );

        (new ErlService).createInvestmentPartner(newContact)
            .then((apiResponse: ApiResponse) => {
                console.log("inquiry saved!", newContact);
                fireEvent('invest-assistant-completed');
                trackLead(apiResponse.response.idSha256, 'Landingpage 1');
                resolve(true);
            })
            .catch(reason => {
                resolve(false);
                console.log(reason);
                return false;
            });

        return true;
    }

    const submitLabel = selectedPath === 'newsletter'
        ? 'JETZT ANMELDEN'
        : selectedPath === 'rueckruf'
            ? 'RÜCKRUF ANFORDERN'
            : 'JETZT ANFRAGEN';

    return <Slider nr={2}>
        <Grid container rowGap={3} justifyContent="center">
            <Grid item xs={12}>
                {selectedPath === 'newsletter' ? (
                    <Typography>
                        Trage dich hier zu unserem Newsletter ein und erhalte <strong> als einer der ersten Infos </strong> zu neuen
                        Bauvorhaben und Investitionsmöglichkeiten. Du kannst ich natürlich jederzeit wieder austragen.
                    </Typography>
                ) : (
                    <>
                        <Typography variant="h3" sx={{ mb: 1 }}>
                            {selectedPath === 'rueckruf'
                                ? 'Unter welcher Telefonnummer dürfen wir dich zurückrufen?'
                                : 'Wie dürfen wir dich kontaktieren?'}
                        </Typography>
                        {selectedPath !== 'rueckruf' && (
                            <Typography sx={{ color: '#6f7a81', fontSize: '.9rem' }}>
                                Gib mindestens deine E-Mail-Adresse oder Telefonnummer an.
                            </Typography>
                        )}
                    </>
                )}

            </Grid>
            <Grid item xs={12}>
                <Label>Dein Name *</Label>
                <FieldInput
                    inputRef={nameInputRef}
                    autoComplete="name"
                    error={!isNameValid(yourName) && formSubmitted}
                    helperText={!isNameValid(yourName) && formSubmitted ? 'Bitte deinen Namen eingeben' : null}
                    fullWidth={true}
                    placeholder="Max Mustermann"
                    value={yourName}
                    variant="standard"
                    onChange={(e) => setYourName(e.target.value)}
                    InputProps={{
                        startAdornment:
                            <InputAdornment position="start">
                                <AccountCircle />
                            </InputAdornment>,
                    }}
                />
            </Grid>
            {isBundeslandRequired && (
            <Grid item xs={12}>
                <Label sx={{ marginBottom: '4px !important' }}>Aus welchem Bundesland kommst du? *</Label>
                <FieldHint>So verbinden wir dich mit einem Berater aus deiner Region.</FieldHint>
                <FieldInput
                    select
                    SelectProps={{ native: true }}
                    autoComplete="address-level1"
                    error={isBundeslandEmpty && formSubmitted}
                    helperText={isBundeslandEmpty && formSubmitted
                        ? 'Bitte wähle dein Bundesland'
                        : null}
                    sx={{ '& .MuiFormHelperText-root': { marginTop: '5px !important' } }}
                    fullWidth={true}
                    value={bundesland}
                    variant="standard"
                    onChange={(e) => setBundesland(e.target.value)}
                    InputProps={{
                        startAdornment:
                            <InputAdornment position="start">
                                <PlaceOutlined />
                            </InputAdornment>,
                    }}
                >
                    <option value="" disabled>Bitte auswählen</option>
                    {BUNDESLAENDER.map((land) => (
                        <option key={land} value={land}>{land}</option>
                    ))}
                </FieldInput>
            </Grid>
            )}
            {selectedPath !== 'rueckruf' && (
            <Grid item xs={12}>
                <Label>Deine E-Mail-Adresse</Label>
                <FieldInput
                    type={"email"}
                    autoComplete="email"
                    error={((isMailEmpty && isMobileEmpty) || (!isMailEmpty && !isMailValid)) && formSubmitted}
                    helperText={((isMailEmpty && isMobileEmpty) || (!isMailEmpty && !isMailValid)) && formSubmitted ? 'Bitte eine gültige E-Mail eingeben' : null}
                    fullWidth={true}
                    placeholder="name@beispiel.de"
                    value={yourMail}
                    variant="standard"
                    onChange={(e) => setYourMail(e.target.value)}
                    InputProps={{
                        startAdornment:
                            <InputAdornment position="start">
                                <AlternateEmail />
                            </InputAdornment>,
                    }}
                />
            </Grid>
            )}
            {selectedPath !== 'newsletter' && (
                <Grid item xs={12}>
                    <Label>Deine Telefonnummer / WhatsApp</Label>
                    <FieldInput
                        type={"tel"}
                        autoComplete="tel"
                        error={((isMailEmpty && isMobileEmpty) || (!isMobileEmpty && !isMobileValid)) && formSubmitted}
                        helperText={((isMailEmpty && isMobileEmpty) || (!isMobileEmpty && !isMobileValid)) && formSubmitted ? 'Bitte eine gültige Mobilnummer eingeben' : null}
                        fullWidth={true}
                        placeholder="+49 170 1234567"
                        value={yourMobile}
                        variant="standard"
                        onChange={(e) => setYourMobile(e.target.value)}
                        InputProps={{
                            startAdornment:
                                <InputAdornment position="start">
                                    <PhoneAndroid />
                                </InputAdornment>,
                        }}
                    />
                </Grid>
            )}
            {isIidConsentRequired && (
                <Grid item xs={12}>
                    <IidConsentBox hasError={isIidConsentMissing && formSubmitted}>
                        <FormControlLabel
                            sx={{ alignItems: 'flex-start', margin: 0 }}
                            control={
                                <Checkbox
                                    checked={iidConsent}
                                    onChange={(e) => setIidConsent(e.target.checked)}
                                    sx={{ paddingTop: '2px', paddingLeft: 0 }}
                                />
                            }
                            label={
                                <IidConsentText>
                                    Wir übermitteln deine Anfrage zur weiteren Bearbeitung und Kontaktaufnahme an
                                    unseren Partner <Link href="https://iid-partner.de/" target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}>IID</Link>.
                                    {' '}Es greifen die Datenschutzrichtlinien der <Link
                                        href="https://www.erl.de/datenschutz/" target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}>ERL Immobilien AG</Link> und der <Link
                                        href="https://iid-partner.de/datenschutz" target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}>IID Immobilien Investment Deutschland
                                        GmbH</Link>. Damit bin ich einverstanden. *
                                </IidConsentText>
                            }
                        />
                        {isIidConsentMissing && formSubmitted && (
                            <IidConsentError>
                                Bitte stimme der Übermittlung an IID zu, damit wir deine Anfrage bearbeiten können.
                            </IidConsentError>
                        )}
                    </IidConsentBox>
                </Grid>
            )}
            <Grid item xs={12}>
                <DataPrivacyNotice>
                    Mit Klick auf "{submitLabel}" erklärst Du Dich mit unseren <Link href="https://www.erl.de/datenschutz/"
                        target="_blank">Datenschutzbestimmungen</Link> einverstanden.
                </DataPrivacyNotice>
            </Grid>
            <Grid item xs={12} sx={{ mt: '-30px' }}>
                <FooterButtons labelForward={submitLabel} increaseStepNumber={true} onForward={() => {
                    return new Promise<boolean>((resolve, reject) => {
                        setFormSubmitted(true);

                        if (isValid()) {
                            saveInquiry(resolve);
                        } else {
                            return resolve(false);
                        }

                    });
                }} />
            </Grid>
            {selectedPath !== 'newsletter' && (
                <Grid item xs={12}>
                    <NewsletterConsentText>
                        Wir möchten dich gerne auch in Zukunft per E-Mail über Investitionsmöglichkeiten und Inhalten zu und über Immobilien informieren und dir
                        gegebenenfalls E-Mails zur Produkt- oder Unternehmensbewertung zusenden. Wenn du keine weiteren E-Mails dieser Art von uns erhalten
                        möchtest, kannst du dich jederzeit abmelden oder dem Versand mit sofortiger Wirkung widersprechen. Hierfür entstehen keine anderen
                        Kosten als die Übermittlungskosten nach den Basistarifen. Für einen schnell Widerspruch, klicke <NewsletterConsentLink
                            onClick={() => setNewsletterConsent(false)}>hier</NewsletterConsentLink>. </NewsletterConsentText>

                    {!newsletterConsent &&
                        <NewsletterConsentText>Dein Widerspruch wurde vorgemerkt. Klicke <NewsletterConsentLink
                            onClick={() => setNewsletterConsent(true)}>hier</NewsletterConsentLink> um deinen Widerspruch zurück zu
                            ziehen.</NewsletterConsentText>
                    }
                </Grid>
            )}
        </Grid>
    </Slider>
}